<script lang="ts">
export default {
  name: 'CoverageTypeCreateModal',
};
</script>

<script setup lang="ts">
import { ref } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';

const props = defineProps<{
  coverageTypes: string[];
}>();

const emit = defineEmits<{
  create: [coverageTypeName: string];
}>();

// Reactive state
const coverageTypeName = ref('');
const error = ref('');
const showModal = ref(false);
const loading = ref(false);

// Methods
const openModal = () => {
  coverageTypeName.value = '';
  error.value = '';
  showModal.value = true;
};

const handleSave = async () => {
  try {
    loading.value = true;
    error.value = '';

    // Validate coverage type name
    if (!coverageTypeName.value.trim()) {
      error.value = 'Coverage type name cannot be empty';
      loading.value = false;
      return;
    }

    if (props.coverageTypes.includes(coverageTypeName.value.trim())) {
      error.value = 'Coverage type already exists';
      loading.value = false;
      return;
    }

    // Emit the create event
    emit('create', coverageTypeName.value.trim());
    showModal.value = false;
    loading.value = false;
  } catch (e) {
    console.error(e);
    loading.value = false;
  }
};

const handleCancel = () => {
  coverageTypeName.value = '';
  error.value = '';
  showModal.value = false;
};
</script>

<template>
  <div>
    <button class="tw:btn tw:btn-primary tw:btn-sm" @click="openModal" data-test="create-coverage-type-btn">
      Create Coverage Type
    </button>

    <AppModal
      :show="showModal"
      title="Create New Coverage Type"
      :loading="loading"
      actionButtonLabel="Create"
      :handleAction="handleSave"
      :handleCancel="handleCancel"
      size="md">
      <template #content>
        <div class="tw:flex tw:flex-col tw:gap-4">
          <div class="tw:w-full">
            <label class="tw:label">
              <span class="tw:label-text tw:text-xs tw:text-gray-500">Coverage Type Name</span>
            </label>
            <input
              type="text"
              v-model="coverageTypeName"
              class="tw:input tw:input-md tw:w-full tw:border tw:border-solid"
              :class="{ 'tw:border-red-500': error }"
              data-test="coverage-type-name-input"
              placeholder="Enter coverage type name..." />
            <label v-if="error" class="tw:label">
              <span class="tw:label-text-alt tw:text-xs tw:text-red-500">{{ error }}</span>
            </label>
          </div>
        </div>
      </template>
    </AppModal>
  </div>
</template>
